import React from 'react';
import {
  Stack,
  Paper,
  Title,
  Text,
  Grid,
  Group,
  Badge,
  Card,
  SimpleGrid,
  Progress,
  Box,
  Center
} from '@mantine/core';
import {
  IconTrendingUp,
  IconCalendarStats,
  IconSchool,
  IconBook,
  IconClock,
  IconChartPie
} from '@tabler/icons-react';
import {
  YearlyCalendarReportStatistics,
  YearlyCalendarCourseReport
} from 'services/yearlyCalendar/reportService';

interface ReportStatisticsProps {
  statistics: YearlyCalendarReportStatistics;
  courses: YearlyCalendarCourseReport[];
}

const ReportStatistics: React.FC<ReportStatisticsProps> = ({ statistics }) => {
  const getPercentage = (value: number, total: number) => {
    return total > 0 ? Math.round((value / total) * 100) : 0;
  };

  const getStatusColor = (status: string | null | undefined) => {
    if (!status) return 'gray';
    switch (status) {
      case 'planned':
        return 'blue';
      case 'canceled':
        return 'red';
      case 'dropped':
        return 'orange';
      default:
        return 'gray';
    }
  };

  return (
    <Stack gap='lg'>
      {/* Overview Cards */}
      <SimpleGrid cols={4} spacing='md'>
        <Card withBorder p='lg'>
          <Group gap='sm'>
            <IconBook size={24} color='#228be6' />
            <div>
              <Text size='xs' c='dimmed' fw={500}>
                Total Courses
              </Text>
              <Text size='xl' fw={700}>
                {statistics.totalCourses}
              </Text>
            </div>
          </Group>
        </Card>

        <Card withBorder p='lg'>
          <Group gap='sm'>
            <IconClock size={24} color='#40c057' />
            <div>
              <Text size='xs' c='dimmed' fw={500}>
                Learning Hours
              </Text>
              <Text size='xl' fw={700}>
                {statistics.totalLearningHours}
              </Text>
            </div>
          </Group>
        </Card>

        <Card withBorder p='lg'>
          <Group gap='sm'>
            <IconTrendingUp size={24} color='#fd7e14' />
            <div>
              <Text size='xs' c='dimmed' fw={500}>
                Average Hours
              </Text>
              <Text size='xl' fw={700}>
                {Math.round(statistics.totalLearningHours / statistics.totalCourses) || 0}
              </Text>
            </div>
          </Group>
        </Card>

        <Card withBorder p='lg'>
          <Group gap='sm'>
            <IconCalendarStats size={24} color='#845ef7' />
            <div>
              <Text size='xs' c='dimmed' fw={500}>
                Planned Courses
              </Text>
              <Text size='xl' fw={700}>
                {statistics.plannedCourses}
              </Text>
            </div>
          </Group>
        </Card>
      </SimpleGrid>

      <Grid>
        {/* Course Status Distribution */}
        <Grid.Col span={6}>
          <Paper p='lg' withBorder>
            <Group gap='sm' mb='md'>
              <IconChartPie size={20} />
              <Title order={4}>Course Status Distribution</Title>
            </Group>

            <Stack gap='md'>
              {Object.entries(statistics.coursesByStatus).map(([status, count]) => {
                const percentage = getPercentage(count, statistics.totalCourses);
                return (
                  <Box key={status}>
                    <Group justify='space-between' mb='xs'>
                      <Group gap='xs'>
                        <Badge variant='filled' color={getStatusColor(status)} size='sm'>
                          {status}
                        </Badge>
                        <Text size='sm'>{count} courses</Text>
                      </Group>
                      <Text size='sm' fw={500}>
                        {percentage}%
                      </Text>
                    </Group>
                    <Progress value={percentage} color={getStatusColor(status)} size='sm' />
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid.Col>

        {/* Delivery Type Distribution */}
        <Grid.Col span={6}>
          <Paper p='lg' withBorder>
            <Group gap='sm' mb='md'>
              <IconBook size={20} />
              <Title order={4}>Delivery Type Distribution</Title>
            </Group>

            <Stack gap='md'>
              {Object.entries(statistics.coursesByDeliveryType).map(([type, count]) => {
                const percentage = getPercentage(count, statistics.totalCourses);
                const color = type === 'modular' ? 'blue' : 'green';
                return (
                  <Box key={type}>
                    <Group justify='space-between' mb='xs'>
                      <Group gap='xs'>
                        <Badge variant='light' color={color} size='sm'>
                          {type}
                        </Badge>
                        <Text size='sm'>{count} courses</Text>
                      </Group>
                      <Text size='sm' fw={500}>
                        {percentage}%
                      </Text>
                    </Group>
                    <Progress value={percentage} color={color} size='sm' />
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid.Col>

        {/* School Distribution */}
        <Grid.Col span={6}>
          <Paper p='lg' withBorder>
            <Group gap='sm' mb='md'>
              <IconSchool size={20} />
              <Title order={4}>Distribution by School</Title>
            </Group>

            <Stack gap='md'>
              {Object.entries(statistics.coursesBySchool)
                .sort(([, a], [, b]) => b - a)
                .slice(0, 5) // Show top 5 schools
                .map(([school, count]) => {
                  const percentage = getPercentage(count, statistics.totalCourses);
                  return (
                    <Box key={school}>
                      <Group justify='space-between' mb='xs'>
                        <Text size='sm' lineClamp={1} style={{ flex: 1 }}>
                          {school}
                        </Text>
                        <Group gap='xs'>
                          <Text size='sm'>{count}</Text>
                          <Text size='sm' fw={500}>
                            {percentage}%
                          </Text>
                        </Group>
                      </Group>
                      <Progress value={percentage} color='indigo' size='sm' />
                    </Box>
                  );
                })}
            </Stack>
          </Paper>
        </Grid.Col>

        {/* Monthly Distribution */}
        <Grid.Col span={6}>
          <Paper p='lg' withBorder>
            <Group gap='sm' mb='md'>
              <IconCalendarStats size={20} />
              <Title order={4}>Monthly Distribution</Title>
            </Group>

            <Stack gap='md'>
              {Object.entries(statistics.monthlyDistribution)
                .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
                .map(([month, count]) => {
                  const percentage = getPercentage(count, statistics.totalCourses);
                  return (
                    <Box key={month}>
                      <Group justify='space-between' mb='xs'>
                        <Text size='sm'>{month}</Text>
                        <Group gap='xs'>
                          <Text size='sm'>{count}</Text>
                          <Text size='sm' fw={500}>
                            {percentage}%
                          </Text>
                        </Group>
                      </Group>
                      <Progress value={percentage} color='teal' size='sm' />
                    </Box>
                  );
                })}

              {Object.keys(statistics.monthlyDistribution).length === 0 && (
                <Center py='xl'>
                  <Text c='dimmed'>No scheduled courses found</Text>
                </Center>
              )}
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Summary */}
      <Paper p='lg' withBorder>
        <Title order={4} mb='md'>
          Summary
        </Title>
        <Grid>
          <Grid.Col span={3}>
            <Text size='sm' c='dimmed'>
              Total Courses
            </Text>
            <Text fw={600}>{statistics.totalCourses}</Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size='sm' c='dimmed'>
              Total Learning Hours
            </Text>
            <Text fw={600}>{statistics.totalLearningHours}</Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size='sm' c='dimmed'>
              Planned Courses
            </Text>
            <Text fw={600} c='blue'>
              {statistics.plannedCourses}
            </Text>
          </Grid.Col>
          <Grid.Col span={3}>
            <Text size='sm' c='dimmed'>
              Completion Rate
            </Text>
            <Text fw={600} c='green'>
              {getPercentage(statistics.plannedCourses, statistics.totalCourses)}%
            </Text>
          </Grid.Col>
        </Grid>
      </Paper>
    </Stack>
  );
};

export default ReportStatistics;
